import {
  AppAvatar,
  AppBox,
  AppButton,
  AppIconButton,
  AppLink,
  AppModal,
  AppPaper,
  AppText,
  AppTitle,
} from "../../../../commons/components";
import AppBorderBox from "../../../../commons/components/BorderBox";
import IconBin from "../../../../commons/components/icons/bin";
import IconEmpty from "../../../../commons/components/icons/empty";
import SkeletonGlobalCardList from "../../../../commons/components/skeleton/SkeletonGlobalCardList";
import { StatusEnum } from "../../../../commons/enums/status-enum";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import { useEffect, useState } from "react";
import { ManageFootballCompetition } from "../../../../commons/models/football/admin/manage-football-competition";
import TournamentDelete from "./TournamentDelete";
import TournamentDeleteInprogress from "./TournamentDeleteInprogress";
import { useSnackbar } from "../../../../redux/snackbarProvider";
import setDocumentTitle from "../../../../commons/utilities/document-title";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../commons/components/interface";
import { competitionDelete } from "../../../../redux/slices/football/admin/competition/manage-competition-delete-slice";
import { fetchManageCompetitionList } from "../../../../redux/slices/football/admin/competition/manage-competition-list-slice";
import TournamentAddEdit from "./tournamentAddEdit/TournamentAddEdit";
import IconEdit from "../../../../commons/components/icons/edit";
import { cleanUpStepperFormData } from "../../../../redux/slices/stepper-form-slice";
import IconTrophy from "../../../../commons/components/icons/trophy";
import { Can } from "../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../commons/enums/role-policy-action.enum";
import FilterToolbar from "../../../../commons/components/ui-components/FilterToolbar";
import useFilter from "../../../../hooks/useFilter";
import { IFootballCompetition } from "../../../../commons/models/football/interface/i-football-competition";
import isNullOrUndefined from "../../../../commons/utilities/null-or-undefined";
import { hostedCompetitionDelete } from "../../../../redux/slices/football/admin/team/hosted-competition/manage-team-hosted-competition-delete-slice";

export default function TournamentList() {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { competitionList, competitionListStatus } = useAppSelector((state) => state.footballManageCompetitionList);
  const { competitionDeleteStatus } = useAppSelector((state) => state.footballManageCompetitionDelete);
  const [isDeleteTournament, setDeleteTournament] = useState(false);
  const [isAddEditTournament, setAddEditTournament] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<IFootballCompetition | null>(null);
  const { searchText, sortOrder, sortBy } = useFilter();
  const { competitionAddResponseStatus } = useAppSelector((state) => state.footballManageCompetitionAdd);
  const { competitionEditResponseStatus } = useAppSelector((state) => state.footballManageCompetitionEdit);

  const handleDeleteClick = (tournament: IFootballCompetition) => (event: any) => {
    setSelectedTournament(tournament);
    setDeleteTournament(true);
  };

  const handleEditClick = (tournament: IFootballCompetition) => (event: any) => {
    setSelectedTournament(tournament);
    setAddEditTournament(true);
  };

  const handleAfterAddEdit = () => {
    setSelectedTournament(null);
    setAddEditTournament(false);
    fetchTournamentList();
  };
  const handleAfterAddEditClose = () => {
    setSelectedTournament(null);
    setAddEditTournament(false);
    dispatch(cleanUpStepperFormData());
  };

  const fetchTournamentList = () => {
    dispatch(fetchManageCompetitionList({ sort: sortBy, sortOrder, name: searchText }));
  };

  const handleDeleteConfirm = (e: Event) => {
    e.preventDefault();
    if (selectedTournament) {
      setDeleteTournament(false);
      if (selectedTournament.hostTeam) {
        dispatch(
          hostedCompetitionDelete({
            teamId: selectedTournament.hostTeam.id,
            hostedCompetitionId: selectedTournament.id,
          })
        )
          .unwrap()
          .then(() => {
            addSnackbar({
              key: "tournament-delete-success",
              text: "Tournament Deleted Successfully",
              variant: "success",
            });
            fetchTournamentList();
          })
          .catch((error: AxiosError<IServerErrorResponse>) => {
            addSnackbar({
              key: "error",
              text: error.response?.data.message,
              variant: "danger",
            });
          });
      } else {
        dispatch(competitionDelete(selectedTournament.id))
          .unwrap()
          .then(() => {
            addSnackbar({
              key: "tournament-delete-success",
              text: "Tournament Deleted Successfully",
              variant: "success",
            });
            fetchTournamentList();
          })
          .catch((error: AxiosError<IServerErrorResponse>) => {
            addSnackbar({
              key: "error",
              text: error.response?.data.message,
              variant: "danger",
            });
          });
      }
      setSelectedTournament(null);
    }
  };

  const handleClick = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    setDocumentTitle(["Tournaments", "Manage"]);
  }, []);

  const getTournamentRoute = (competition: IFootballCompetition): string => {
    if (!isNullOrUndefined(competition.hostTeam)) {
      return `/manage/teams/${competition.hostTeam?.id}/hosted-tournaments/${competition.id}`;
    }
    return `/manage/tournaments/${competition.id}`;
  };

  return (
    <>
      <AppBox flexDirection="column" gap="xs">
        <AppBox alignItems="center" justifyContent="space-between">
          <AppTitle as="h4">All Tournaments</AppTitle>
          <Can I={RolePolicyActionEnum.CREATE} a={ManageFootballCompetition}>
            {competitionList.length > 0 && (
              <AppButton label="Add Tournament" onClick={() => setAddEditTournament(true)} />
            )}
          </Can>
        </AppBox>
        <FilterToolbar
          enableStatus={false}
          sortByOptions={[
            {
              title: "Title",
              value: "title",
            },
            {
              title: "Created Date",
              value: "createdAt",
            },
          ]}
        />
        {competitionListStatus === StatusEnum.Loading ? (
          <SkeletonGlobalCardList noTitle numberOfItems={10} withAvatar withBadge />
        ) : (
          <>
            {competitionList.length === 0 ? (
              <AppPaper style={{ height: "100%" }}>
                <AppBorderBox borderVariant="dashed">
                  <AppBox
                    flexDirection="column"
                    alignItems="center"
                    gap="sm"
                    justifyContent="center"
                    style={{ height: "calc(100vh - 16rem)" }}
                  >
                    <AppBox alignItems="center" flexDirection="column" justifyContent="center" gap="2xs">
                      <IconEmpty width={56} height={56} color="rgba(var(--border-200))" />
                      <AppTitle as="h6">No Tournaments Found</AppTitle>
                      <Can I={RolePolicyActionEnum.CREATE} a={ManageFootballCompetition}>
                        <AppText>Ready to kick things off? Let's get started by adding tournaments.</AppText>
                      </Can>
                    </AppBox>
                    <Can I={RolePolicyActionEnum.CREATE} a={ManageFootballCompetition}>
                      <AppButton label="Add New Tournament" onClick={() => setAddEditTournament(true)} />
                    </Can>
                  </AppBox>
                </AppBorderBox>
              </AppPaper>
            ) : (
              <>
                {competitionList.mapArray((competition, index) => (
                  <AppPaper radius="sm" padding="none" shadow="xs" key={index}>
                    <AppBox gap="xs" my="2xs" mx="xs" justifyContent="space-between">
                      <AppLink to={getTournamentRoute(competition)} onClick={handleClick}>
                        <AppBox gap="xs" alignItems="center">
                          <AppAvatar username={competition.title} src={competition.logo} size="lg" />
                          <AppBox flexDirection="column">
                            <AppTitle as={"h5"}>{competition.title}</AppTitle>
                            {(competition.organizer || competition.hostTeam) && (
                              <AppBox gap="3xs">
                                <AppText fontWeight="semibold">Organizer:</AppText>
                                <AppText color="muted">
                                  {competition.organizer?.name ?? competition.hostTeam?.shortName}
                                </AppText>
                              </AppBox>
                            )}
                          </AppBox>
                        </AppBox>
                      </AppLink>
                      <AppBox gap="xs" justifyContent="end" alignItems="center">
                        <Can I={RolePolicyActionEnum.UPDATE} this={competition}>
                          <AppIconButton icon={<IconEdit />} variant="light" onClick={handleEditClick(competition)} />
                        </Can>
                        <Can I={RolePolicyActionEnum.DELETE} this={competition}>
                          <AppIconButton
                            icon={<IconBin />}
                            variant="light"
                            color="danger"
                            onClick={handleDeleteClick(competition)}
                          />
                        </Can>
                      </AppBox>
                    </AppBox>
                  </AppPaper>
                ))}
              </>
            )}
          </>
        )}
      </AppBox>
      {selectedTournament && (
        <>
          <AppModal
            opened={isDeleteTournament}
            onClose={() => {
              setSelectedTournament(null);
              setDeleteTournament(false);
            }}
            title="Want To Delete This Tournament?"
            titleIcon={<IconBin />}
            iconColor="danger"
            confirmButtonLabel="Delete"
            onSave={(e) => {
              handleDeleteConfirm(e as Event);
            }}
          >
            <TournamentDelete tournament={selectedTournament} />
          </AppModal>
          <AppModal
            opened={competitionDeleteStatus === StatusEnum.Loading}
            title="Deleting Tournament"
            titleIcon={<IconBin />}
            iconColor="danger"
            withoutCancelButton
            withoutButtons
            withoutCloseButton
          >
            <TournamentDeleteInprogress tournament={selectedTournament} />
          </AppModal>
        </>
      )}
      <AppModal
        opened={isAddEditTournament}
        modalSize="lg"
        onClose={handleAfterAddEditClose}
        title={selectedTournament ? "Edit Tournament" : "Add New Tournament"}
        titleIcon={selectedTournament ? <IconEdit /> : <IconTrophy />}
        withoutButtons
        withLoadingButton={
          competitionAddResponseStatus === StatusEnum.Loading || competitionEditResponseStatus === StatusEnum.Loading
        }
      >
        <TournamentAddEdit
          onSave={handleAfterAddEdit}
          competitionData={selectedTournament}
          hostTeam={selectedTournament?.hostTeam}
        />
      </AppModal>
    </>
  );
}
